import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, Plus, Trash2, Download, AlertTriangle, CheckCircle2, Clock, XCircle, Circle } from 'lucide-react';
import { documentsMatrixApi, pdvApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';

const STATUS_COLORS = {
  valid:    { bg: 'bg-emerald-500',  border: 'border-emerald-700',  label: 'Valido',       icon: CheckCircle2 },
  expiring: { bg: 'bg-amber-500',    border: 'border-amber-700',    label: 'In scadenza',  icon: Clock },
  expired:  { bg: 'bg-red-600',      border: 'border-red-800',      label: 'Scaduto',      icon: XCircle },
  missing:  { bg: 'bg-red-500',      border: 'border-red-700',      label: 'Mancante',     icon: AlertTriangle },
  na:       { bg: 'bg-gray-400',     border: 'border-gray-600',     label: 'N/A',          icon: Circle },
};

export default function DocumentsMatrixPage() {
  const { user } = useAuth();
  const { currentPdvId } = useCurrentPdv();
  const [loading, setLoading] = useState(true);
  const [matrix, setMatrix] = useState(null);
  const [aziendaFilter, setAziendaFilter] = useState('all');
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [searchType, setSearchType] = useState('');
  const [searchUl, setSearchUl] = useState('');
  const [aziende, setAziende] = useState([]);

  // Manage types dialog
  const [typesDialog, setTypesDialog] = useState(false);
  const [types, setTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [newHasExpiry, setNewHasExpiry] = useState(false);
  const [newValidityMonths, setNewValidityMonths] = useState('');

  const canManage = ['super_admin', 'admin', 'amministratore'].includes(user?.role);

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const params = {};
      if (aziendaFilter !== 'all') params.azienda = aziendaFilter;
      if (onlyIssues) params.only_with_issues = true;
      // UL Attiva: se selezionata, filtra la matrice su quella sola UL
      if (currentPdvId) params.pdv_id = currentPdvId;
      const r = await documentsMatrixApi.getMatrix(params);
      setMatrix(r.data);
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento della matrice');
    } finally {
      setLoading(false);
    }
  };

  const loadAziende = async () => {
    try {
      const r = await pdvApi.getAll();
      const set = new Set();
      (r.data || []).forEach((p) => p.company_name && set.add(p.company_name));
      setAziende(Array.from(set).sort());
    } catch (e) {
      // optional
    }
  };

  const loadTypes = async () => {
    try {
      const r = await documentsMatrixApi.listTypes();
      setTypes(r.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadAziende(); loadTypes(); }, []);
  useEffect(() => { loadMatrix(); /* eslint-disable-next-line */ }, [aziendaFilter, onlyIssues, currentPdvId]);

  // Filtered ULs and types (client-side)
  const filteredTypes = useMemo(() => {
    if (!matrix) return [];
    const q = searchType.trim().toLowerCase();
    if (!q) return matrix.doc_types;
    return matrix.doc_types.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [matrix, searchType]);

  const filteredUls = useMemo(() => {
    if (!matrix) return [];
    const q = searchUl.trim().toLowerCase();
    if (!q) return matrix.uls;
    return matrix.uls.filter((u) =>
      (u.name || '').toLowerCase().includes(q)
      || (u.code || '').toLowerCase().includes(q)
      || (u.city || '').toLowerCase().includes(q)
    );
  }, [matrix, searchUl]);

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      toast.error('Nome obbligatorio');
      return;
    }
    try {
      await documentsMatrixApi.createType({
        name: newTypeName.trim(),
        has_expiry: newHasExpiry,
        default_validity_months: newValidityMonths ? parseInt(newValidityMonths, 10) : null,
      });
      toast.success('Tipologia aggiunta');
      setNewTypeName('');
      setNewHasExpiry(false);
      setNewValidityMonths('');
      await loadTypes();
      await loadMatrix();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiunta');
    }
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Eliminare/disattivare questa tipologia?')) return;
    try {
      await documentsMatrixApi.deleteType(id);
      toast.success('Tipologia rimossa');
      await loadTypes();
      await loadMatrix();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleExportCsv = () => {
    if (!matrix) return;
    const sep = ';';
    const header = ['Documento', ...filteredUls.map(u => `${u.code} - ${u.name}`)].join(sep);
    const lines = [header];
    for (const t of filteredTypes) {
      const row = [t.name];
      for (const u of filteredUls) {
        const cell = matrix.matrix[t.code]?.[u.id];
        row.push(cell ? cell.status : 'missing');
      }
      lines.push(row.join(sep));
    }
    const blob = new Blob(["\ufeff" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matrice_documenti_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading && !matrix) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 lg:p-6" data-testid="documents-matrix-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Matrice Documenti × Unità Locali</h1>
          <p className="text-sm text-muted-foreground">Stato di compliance documentale per ogni UL</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Esporta CSV
          </Button>
          {canManage && (
            <Button variant="outline" onClick={() => setTypesDialog(true)} data-testid="manage-types-btn">
              <Plus className="w-4 h-4 mr-1" /> Gestisci Tipologie
            </Button>
          )}
        </div>
      </div>

      {/* KPI summary */}
      {matrix?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            { k: 'valid',    name: 'Validi' },
            { k: 'expiring', name: 'In scadenza' },
            { k: 'expired',  name: 'Scaduti' },
            { k: 'missing',  name: 'Mancanti' },
            { k: 'na',       name: 'N/A' },
          ].map(({ k, name }) => {
            const conf = STATUS_COLORS[k];
            const Icon = conf.icon;
            return (
              <Card key={k} className="border-0 shadow-sm">
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`${conf.bg} w-10 h-10 rounded-full flex items-center justify-center text-white`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">{name}</div>
                    <div className="text-xl font-bold" data-testid={`kpi-${k}`}>{matrix.summary[k] ?? 0}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filtri */}
      <Card>
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Azienda</Label>
            <Select value={aziendaFilter} onValueChange={setAziendaFilter}>
              <SelectTrigger data-testid="azienda-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le aziende</SelectItem>
                {aziende.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Cerca tipologia</Label>
            <Input placeholder="Es. APE, CPI..." value={searchType} onChange={(e) => setSearchType(e.target.value)} data-testid="search-type-input" />
          </div>
          <div>
            <Label className="text-xs">Cerca UL</Label>
            <Input placeholder="Nome / codice / città" value={searchUl} onChange={(e) => setSearchUl(e.target.value)} data-testid="search-ul-input" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} data-testid="only-issues-toggle" />
              <span className="text-sm">Solo problemi (mancanti / scaduti / in scadenza)</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Legenda */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(STATUS_COLORS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1">
            <div className={`${v.bg} w-3 h-3 rounded-full`} /> {v.label}
          </div>
        ))}
      </div>

      {/* Tabella */}
      {!matrix || matrix.uls.length === 0 || matrix.doc_types.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Nessun dato da mostrare.
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg overflow-auto" style={{ maxHeight: '70vh' }} data-testid="matrix-table-container">
          <table className="text-xs border-collapse w-max">
            <thead>
              <tr className="bg-muted">
                <th
                  className="sticky left-0 top-0 z-30 bg-muted px-3 py-2 text-left font-semibold border-b border-r min-w-[260px]"
                  data-testid="th-document"
                >
                  Documento / Unità Locali
                </th>
                {filteredUls.map(u => (
                  <th
                    key={u.id}
                    className="sticky top-0 z-20 bg-muted px-2 py-2 text-center font-semibold border-b border-r whitespace-nowrap"
                    title={`${u.name} – ${u.city}`}
                  >
                    <div className="text-[10px] text-muted-foreground">{u.code}</div>
                    <div className="max-w-[90px] truncate">{u.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTypes.map(t => (
                <tr key={t.code} className="even:bg-muted/30">
                  <td className="sticky left-0 z-10 bg-background px-3 py-1.5 border-r font-medium" data-testid={`row-${t.code}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span>{t.name}</span>
                      {t.has_expiry && <span className="text-[9px] px-1 rounded bg-amber-100 text-amber-800">scad.</span>}
                    </div>
                  </td>
                  {filteredUls.map(u => {
                    const cell = matrix.matrix[t.code]?.[u.id];
                    if (!cell) {
                      return <td key={u.id} className="border-r border-b px-1 py-1 text-center"><div className="w-4 h-4 bg-gray-300 rounded-full inline-block" /></td>;
                    }
                    const conf = STATUS_COLORS[cell.status] || STATUS_COLORS.missing;
                    const tooltip = `${t.name}\n${u.name}\nStato: ${conf.label}${cell.expiry_date ? `\nScadenza: ${cell.expiry_date}` : ''}${cell.days_left != null ? `\nGiorni: ${cell.days_left}` : ''}`;
                    return (
                      <td key={u.id} className="border-r border-b px-1 py-1 text-center" data-testid={`cell-${t.code}-${u.id}`}>
                        <div
                          className={`${conf.bg} ${conf.border} w-4 h-4 rounded-full inline-block border cursor-help`}
                          title={tooltip}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manage Types Dialog */}
      <Dialog open={typesDialog} onOpenChange={setTypesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="types-dialog">
          <DialogHeader>
            <DialogTitle>Tipologie di Documento Richieste</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {canManage && (
              <div className="p-3 rounded-lg border bg-blue-50/40 space-y-2">
                <Label className="text-xs">Aggiungi tipologia personalizzata</Label>
                <Input
                  placeholder="Es. Certificato impianto idraulico"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  data-testid="new-type-name"
                />
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={newHasExpiry} onChange={(e) => setNewHasExpiry(e.target.checked)} data-testid="new-has-expiry" />
                    Ha scadenza
                  </label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Validità (mesi)"
                    value={newValidityMonths}
                    onChange={(e) => setNewValidityMonths(e.target.value)}
                    disabled={!newHasExpiry}
                    data-testid="new-validity-months"
                  />
                </div>
                <Button size="sm" onClick={handleAddType} data-testid="add-type-btn">
                  <Plus className="w-3 h-3 mr-1" /> Aggiungi
                </Button>
              </div>
            )}

            <div className="border rounded-lg divide-y">
              {types.map(t => (
                <div key={t.id} className="flex items-center justify-between p-2 text-sm">
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {t.code} · {t.has_expiry ? `Scad. (${t.default_validity_months || '?'} mesi)` : 'Senza scadenza'}
                      {t.is_custom && <span className="ml-1 px-1 rounded bg-purple-100 text-purple-800">custom</span>}
                    </div>
                  </div>
                  {canManage && (
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteType(t.id)} data-testid={`delete-type-${t.code}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
